import { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import {
  EffectComposer,
  RenderPass,
  BloomEffect,
  EffectPass,
} from "postprocessing";
import { DitheringEffect } from "./dithering-shader/DitheringEffect.ts";

interface PostProcessingProps {
  params?: {
    bloom1?: any;
    dithering?: any;
    bloom2?: any;
  };
}

/**
 * Component that manages all post-processing effects
 * (Leva panel removed – uses props for fixed parameters)
 */
export const PostProcessing = ({ params }: PostProcessingProps) => {
  const composerRef = useRef<EffectComposer | null>(null);
  const [scene, setScene] = useState<THREE.Scene | null>(null);
  const [camera, setCamera] = useState<THREE.Camera | null>(null);

  // Gelen parametreler yoksa varsayılan değerler
  const bloom1 = params?.bloom1 ?? {
    enabled: false,
    threshold: 0.0,
    intensity: 2.0,
    radius: 0.6,
  };

  const dithering = params?.dithering ?? {
    gridSize: 4,
    pixelSizeRatio: 1,
    grayscaleOnly: true,
    tintColor: "#ffffff",
  };

  const bloom2 = params?.bloom2 ?? {
    enabled: false,
    threshold: 0.0,
    intensity: 0.42,
    radius: 0.75,
    smoothing: 0.22,
  };

  const handleResize = useCallback(() => {
    if (composerRef.current) {
      composerRef.current.setSize(window.innerWidth, window.innerHeight);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    if (!scene || !camera || !composerRef.current) return;

    const composer = composerRef.current;
    composer.removeAllPasses();

    // Render pass
    const renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);

    // Optional Bloom 1
    if (bloom1.enabled) {
      composer.addPass(
        new EffectPass(
          camera,
          new BloomEffect({
            luminanceThreshold: bloom1.threshold,
            intensity: bloom1.intensity,
            radius: bloom1.radius,
            mipmapBlur: true,
          })
        )
      );
    }

    // Dithering (always on)
    composer.addPass(
      new EffectPass(
        camera,
        new DitheringEffect({
          gridSize: dithering.effectResolution ?? dithering.gridSize,
          pixelSizeRatio: dithering.pixelation ?? dithering.pixelSizeRatio,
          grayscaleOnly: dithering.grayscale ?? dithering.grayscaleOnly,
          tintColor: dithering.tintColor ?? "#ffffff",
        })
      )
    );

    // Optional Bloom 2
    if (bloom2.enabled) {
      composer.addPass(
        new EffectPass(
          camera,
          new BloomEffect({
            luminanceThreshold: bloom2.threshold,
            intensity: bloom2.intensity,
            luminanceSmoothing: bloom2.smoothing,
            radius: bloom2.radius,
          })
        )
      );
    }
  }, [scene, camera, params]);

  useFrame(({ gl, scene: currentScene, camera: currentCamera }) => {
    if (!composerRef.current) {
      composerRef.current = new EffectComposer(gl, {
        depthBuffer: true,
        stencilBuffer: false,
      });
      handleResize();
    }
    gl.autoClearColor = false;
    gl.clearColor();
    gl.clear(true, true, true);

    if (scene !== currentScene) setScene(currentScene);
    if (camera !== currentCamera) setCamera(currentCamera);

    composerRef.current?.render();
  }, 1);

  return null;
};
